import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { FileText, TrendingUp, Calendar, Package, DollarSign, Clock, Eye, ArrowRight, CheckCircle, XCircle, Hourglass, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';

const QuotesList = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await api.get('/quotes/my');
      setQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted': return <CheckCircle className="h-4 w-4" />;
      case 'declined': return <XCircle className="h-4 w-4" />;
      case 'viewed': return <Eye className="h-4 w-4" />;
      default: return <Hourglass className="h-4 w-4" />;
    }
  };

  const filteredQuotes = (quotes || []).filter(quote => {
    if (activeFilter === 'all') return true;
    return quote.status === activeFilter;
  });

  const stats = {
    total: (quotes || []).length,
    accepted: (quotes || []).filter(q => q.status === 'accepted').length,
    pending: (quotes || []).filter(q => q.status === 'sent' || q.status === 'viewed' || q.status === 'published').length,
    declined: (quotes || []).filter(q => q.status === 'declined').length
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-4xl font-outfit font-bold text-ocean-900">My Quotes</h1>
              <p className="text-slate-600 mt-2">Track all your submitted quotes and their status</p>
            </div>
            <Button 
              onClick={() => navigate('/quotes/create')}
              className="bg-green-500 hover:bg-green-600"
              data-testid="create-quote-btn"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Create New Quote
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveFilter('all')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Quotes</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  </div>
                  <FileText className="h-8 w-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveFilter('accepted')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Accepted</p>
                    <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveFilter('sent')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Pending</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.pending}</p>
                  </div>
                  <Hourglass className="h-8 w-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveFilter('declined')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Declined</p>
                    <p className="text-2xl font-bold text-red-600">{stats.declined}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6 flex-wrap">
            {['all', 'sent', 'viewed', 'accepted', 'declined'].map((filter) => (
              <Button
                key={filter}
                variant={activeFilter === filter ? 'default' : 'outline'}
                onClick={() => setActiveFilter(filter)}
                className={activeFilter === filter ? 'bg-cyan-500 hover:bg-cyan-600' : ''}
                size="sm"
              >
                {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
              </Button>
            ))}
          </div>

          {filteredQuotes.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-slate-600">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p className="mb-2">
                  {activeFilter === 'all' ? 'No quotes submitted yet' : `No ${activeFilter} quotes`}
                </p>
                {activeFilter === 'all' && (
                  <>
                    <p className="text-sm mb-4">Browse RFQs to submit your first quote</p>
                    <Button onClick={() => navigate('/rfqs')} className="bg-cyan-500 hover:bg-cyan-600">
                      Browse RFQ Marketplace
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredQuotes.map((quote) => (
                <Card 
                  key={quote.id} 
                  className={`hover:shadow-lg transition-all cursor-pointer border-l-4 ${
                    quote.status === 'accepted' ? 'border-l-green-500 bg-green-50/30' :
                    quote.status === 'declined' ? 'border-l-red-500 bg-red-50/30' :
                    quote.status === 'viewed' ? 'border-l-blue-500' :
                    quote.status === 'published' ? 'border-l-purple-500 bg-purple-50/30' :
                    'border-l-slate-300'
                  }`}
                  onClick={() => quote.rfq_id ? navigate(`/rfqs/${quote.rfq_id}`) : null}
                  data-testid={`quote-card-${quote.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className={
                            quote.status === 'accepted' ? 'bg-green-100 text-green-700' :
                            quote.status === 'declined' ? 'bg-red-100 text-red-700' :
                            quote.status === 'viewed' ? 'bg-blue-100 text-blue-700' :
                            quote.status === 'published' ? 'bg-purple-100 text-purple-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {getStatusIcon(quote.status)}
                            <span className="ml-1">{(quote.status || 'pending').toUpperCase()}</span>
                          </Badge>
                          {quote.rfq_id ? (
                            <span className="text-sm text-slate-500">
                              RFQ #{quote.rfq_id.slice(0, 8)}...
                            </span>
                          ) : quote.is_marketplace ? (
                            <span className="text-sm text-purple-500 font-medium">
                              Marketplace Quote
                            </span>
                          ) : null}
                        </div>
                        
                        {/* Route Information */}
                        {(quote.origin || quote.destination) && (
                          <div className="flex items-center gap-2 text-base font-medium text-slate-800 mb-2">
                            <MapPin className="h-4 w-4 text-cyan-500" />
                            <span>{quote.origin || 'N/A'}</span>
                            <ArrowRight className="h-4 w-4 text-slate-400" />
                            <span>{quote.destination || 'N/A'}</span>
                            {quote.cargo_type && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                {quote.cargo_type}
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Submitted {new Date(quote.created_at).toLocaleDateString()}
                          </span>
                          {quote.transit_time_days && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {quote.transit_time_days} days transit
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-sm text-slate-600">Quote Amount</p>
                          <p className="text-2xl font-bold text-green-600">
                            {quote.pricing ? `${quote.pricing.currency} ${quote.pricing.total.toFixed(2)}` : 
                             quote.total_price ? `${quote.currency || 'USD'} ${quote.total_price.toFixed(2)}` : 'N/A'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-600">Valid Until</p>
                          <p className="font-medium">
                            {quote.expires_at ? new Date(quote.expires_at).toLocaleDateString() : 
                             quote.validity_days ? new Date(new Date(quote.created_at).getTime() + quote.validity_days * 24 * 60 * 60 * 1000).toLocaleDateString() :
                             '30 days'}
                          </p>
                        </div>
                        <ArrowRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>

                    {quote.notes && (
                      <p className="mt-3 pt-3 border-t text-sm text-slate-600 line-clamp-1">
                        <span className="font-medium">Note:</span> {quote.notes}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default QuotesList;
